import * as pdfjsLib from "pdfjs-dist";

// TTL for localStorage persistence (best-effort expiration)
const LOCAL_STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

// Key for the persistence preference
const PERSISTENCE_ENABLED_KEY = "fhi_persistence_enabled";

// Interface for stored values with TTL
interface StoredValueWithTTL {
  value: string;
  expiry: number;
}

// This browser's storage, or null where the browser blocks it. Where a
// browser blocks this site's storage, reading window.localStorage throws (a
// SecurityError), and with storage turned off it can be null.
function browserStorage(): Storage | null {
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

// Check if persistence is enabled (default: true). Storage the browser
// blocks reads as turned off, so the page still sets up: nothing is kept,
// and nothing is read back.
function isPersistenceEnabled(): boolean {
  const storage = browserStorage();
  if (storage === null) {
    return false;
  }
  try {
    // Default to true if not set
    return storage.getItem(PERSISTENCE_ENABLED_KEY) !== "false";
  } catch {
    return false;
  }
}

// Set persistence preference, and say whether this browser now keeps what
// is typed. Never throws: where the browser blocks storage nothing can be
// kept, so the answer is false, and a write that fails (full storage) leaves
// the preference as it was.
function setPersistenceEnabled(enabled: boolean): boolean {
  const storage = browserStorage();
  if (storage === null) {
    return false;
  }
  if (!enabled) {
    // Clear all form data when disabling persistence. First, so a full
    // storage has room for the preference.
    try {
      clearFormData();
    } catch {
      // The preference below is still written.
    }
  }
  try {
    storage.setItem(PERSISTENCE_ENABLED_KEY, enabled.toString());
  } catch {
    // Not written; the preference read back below is the one in force.
  }
  return isPersistenceEnabled();
}

// Clear all stored form data (but keep the persistence preference)
// The keys the scrubber writes when it recognises something in a denial
// letter. They live here, next to the clear, so a new scrubbing rule cannot
// introduce a key that the clear does not know about: the rule table's key
// column is typed as this union, so adding one without adding it here fails
// the type check.
const SCRUBBER_STORAGE_KEYS = ["name", "subscriber_id", "group_id"] as const;
// Derived from the list, not declared beside it: two declarations could
// drift, and a key in the union but missing from the list would be stored
// and never cleared (CodeRabbit).
type ScrubberStorageKey = (typeof SCRUBBER_STORAGE_KEYS)[number];

function clearFormData(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (
      key &&
      (key.startsWith("store_") ||
        key === "email" ||
        key === "denial_text" ||
        (SCRUBBER_STORAGE_KEYS as readonly string[]).includes(key))
    ) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => window.localStorage.removeItem(key));
}

// Get item with TTL check. Null where storage is blocked or a read of it
// throws: the field is just not put back.
function getLocalStorageItemWithTTL(key: string): string | null {
  const storage = browserStorage();
  if (storage === null) {
    return null;
  }
  let stored: string | null;
  try {
    // If someones disabled persistence remove items if found.
    if (!isPersistenceEnabled()) {
      storage.removeItem(key);
      return null;
    }
    stored = storage.getItem(key);
  } catch {
    return null;
  }
  if (!stored) {
    return null;
  }

  try {
    const parsed: StoredValueWithTTL = JSON.parse(stored);
    // Only trust the parse when it has the wrapper's actual shape. Raw
    // values that happen to be valid JSON -- a digit-only subscriber or
    // group ID like "123456789" stored directly by scrubText -- parse to a
    // number, whose .value is undefined; returning that put the literal
    // string "undefined" into finished appeal letters.
    if (
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      "value" in parsed
    ) {
      if (parsed.expiry && Date.now() > parsed.expiry) {
        // Item has expired, remove it
        try {
          storage.removeItem(key);
        } catch {
          // Gone or not, it is not put back.
        }
        return null;
      }
      return parsed.value;
    }
    // Valid JSON but not our wrapper: treat as a legacy raw value.
    return stored;
  } catch {
    // Backwards compatibility: if it's not JSON, return the raw value
    return stored;
  }
}

// Set item with TTL
function setLocalStorageItemWithTTL(key: string, value: string): void {
  if (!isPersistenceEnabled()) {
    return;
  }

  const item: StoredValueWithTTL = {
    value: value,
    expiry: Date.now() + LOCAL_STORAGE_TTL_MS,
  };
  window.localStorage.setItem(key, JSON.stringify(item));
}

// The letter box an AI assistant's handoff filled in (/from-your-assistant,
// marked data-from-assistant on the textarea). The server deleted its copy as
// the page opened and the page is the answer to a POST, so a reload would
// lose the text. It is saved once on load the way typing saves it: only
// while "Remember what I typed" is on, for the same 24 hours, and a later
// /scan restores it into the empty box. Returns whether it saved.
function keepServerFilledText(textarea: HTMLTextAreaElement): boolean {
  if (textarea.getAttribute("data-from-assistant") !== "true") {
    return false;
  }
  if (textarea.value === "" || !isPersistenceEnabled()) {
    return false;
  }
  setLocalStorageItemWithTTL(textarea.id, textarea.value);
  return true;
}

// Get item with TTL check and default value
function getLocalStorageItemOrDefault(
  key: string,
  defaultValue: string,
): string {
  const value = getLocalStorageItemWithTTL(key);
  return value !== null ? value : defaultValue;
}

function getLocalStorageItemOrDefaultEQ(key: string): string {
  return getLocalStorageItemOrDefault(key, key);
}

const storeLocal = async function (event: Event) {
  const target = event.target as HTMLInputElement;
  const name = target.id;
  const value = target.value;
  setLocalStorageItemWithTTL(name, value);
};

// Store textarea content with TTL
const storeTextareaLocal = async function (event: Event) {
  const target = event.target as HTMLTextAreaElement;
  const name = target.id;
  const value = target.value;
  setLocalStorageItemWithTTL(name, value);
};

// Runtime worker assets (pdf.js, tesseract.js) are copied into dist/workers/
// by the webpack build (see webpack.config.js). They must never be loaded
// from the third-party package tree under static/js: it is no longer
// collected and nginx denies it outright, which is what broke PDF loading
// and OCR in production. tests/async-unit/test_worker_assets.py pins this.
const workers_path = "/static/js/dist/workers/";

// pdf.js. The worker is published under a .js name (see webpack.config.js):
// nginx serves .mjs as application/octet-stream, which no browser will run as
// a module worker, and that broke every PDF upload in production.
pdfjsLib.GlobalWorkerOptions.workerSrc = workers_path + "pdf.worker.min.js";

/**
 * Get CSRF token from cookies for Django requests.
 * @returns The CSRF token string or empty string if not found
 */
export function getCSRFToken(): string {
  const name = "csrftoken";
  let cookieValue = "";
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + "=") {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

export type { ScrubberStorageKey };
export {
  SCRUBBER_STORAGE_KEYS,
  storeLocal,
  storeTextareaLocal,
  pdfjsLib,
  workers_path,
  getLocalStorageItemOrDefault,
  getLocalStorageItemOrDefaultEQ,
  getLocalStorageItemWithTTL,
  setLocalStorageItemWithTTL,
  keepServerFilledText,
  isPersistenceEnabled,
  setPersistenceEnabled,
  clearFormData,
};
